import { motion } from "framer-motion";
import { Mail, HelpCircle, Send } from "lucide-react";
import SectionBackground from "@/components/layout/SectionBackground";
import SectionTitle from "@/components/layout/SectionTitle";
import FluidCursor from "@/components/animations/FluidCursor";
import CallToAction from "@/components/home/CallToAction";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { theme } from "@/theme";
import { useIsMobile } from "@/hooks/use-mobile";
import { useState, FormEvent } from "react";
import { supabase } from "@/services/supabase";
import { toast } from "sonner";

const Support = () => {
  const isMobile = useIsMobile();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    honeypot: "" // Bot protection field
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Bot protection
    if (formData.honeypot !== "") {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Call edge function to send email
      const { data, error } = await supabase.functions.invoke('submit-support-request', {
        body: {
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          honeypot: formData.honeypot
        }
      });
      
      if (error) {
        // Check for rate limiting error
        if (error.message?.includes('Rate limit') || error.message?.includes('Too many requests')) {
          toast.error('You\'ve submitted too many requests. Please wait an hour before trying again.');
          return;
        }
        
        toast.error('Failed to send message. Please try again or email us directly at klauyros@gmail.com');
        return;
      }
      
      // Check response data for rate limit errors
      if (data?.error) {
        if (data.retryAfter) {
          toast.error('You\'ve submitted too many requests. Please wait an hour before trying again.');
        } else {
          toast.error(data.error);
        }
        return;
      }
      
      // Success!
      toast.success('Message sent successfully! We\'ll get back to you within 24 hours.');
      
      // Reset form
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
        honeypot: ""
      });
      
    } catch (error) {
      toast.error('Failed to send message. Please try again or email us directly at klauyros@gmail.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const faqs = [
    {
      question: "How do I join a course?",
      answer: "Simply browse our courses page, select the course that interests you, and click 'Start Learning'. You'll be redirected to create an account or log in to access the course materials."
    },
    {
      question: "What experience level do I need?",
      answer: "We offer courses for all levels! Each course listing clearly indicates whether it's for beginners, intermediate, or advanced dancers. Start wherever you feel comfortable and progress at your own pace."
    },
    {
      question: "Can I access courses on mobile?",
      answer: "Absolutely! Our platform is fully responsive and optimized for mobile devices. You can learn and practice anywhere, anytime from your smartphone or tablet."
    },
    {
      question: "How long do I have access to a course?",
      answer: "Course access is provided on a monthly subscription basis. As long as your subscription is active, you can learn at your own pace and revisit the material whenever you need a refresher."
    },
    {
      question: "Can I get a certificate after completing a course?",
      answer: "Yes! Upon successful completion of a course, you'll receive a digital certificate that you can share on your social media profiles or add to your portfolio."
    }
  ];

  return (
    <main className="relative">
      <FluidCursor />
      
      {/* Hero Section */}
      <section className="relative py-24 px-4 min-h-[40vh] flex items-center">
        <SectionBackground />
        <div className="relative max-w-7xl mx-auto w-full text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <SectionTitle
              title="We're Here to Help"
              accent="Help"
              subtitle="Have questions? Need support? Our team is ready to assist you on your dance journey."
              align="center"
              size="xl"
            />
          </motion.div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="relative py-16 px-4">
        <SectionBackground />
        <div className="relative max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Card className="border-0 bg-black/40 backdrop-blur-sm">
              <CardHeader>
                <div className="flex items-center mb-2">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center mr-4">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl text-white">Contact Support</CardTitle>
                    <CardDescription className="text-gray-400">
                      Send us a message and we'll get back to you soon
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Honeypot field - hidden from users, visible to bots */}
                  <div className="absolute opacity-0 pointer-events-none" aria-hidden="true">
                    <Label htmlFor="website">Website</Label>
                    <Input
                      id="website"
                      name="honeypot"
                      type="text"
                      value={formData.honeypot}
                      onChange={handleChange}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-white text-sm font-medium">
                      Name
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-orange-500 focus:ring-orange-500"
                      placeholder="Your name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-white text-sm font-medium">
                      Email
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-orange-500 focus:ring-orange-500"
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject" className="text-white text-sm font-medium">
                      Subject
                    </Label>
                    <Input
                      id="subject"
                      name="subject"
                      type="text"
                      required
                      value={formData.subject}
                      onChange={handleChange}
                      className="bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-orange-500 focus:ring-orange-500"
                      placeholder="How can we help?"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-white text-sm font-medium">
                      Message
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      rows={6}
                      className="bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-orange-500 focus:ring-orange-500 resize-none"
                      placeholder="Tell us more about your inquiry..."
                    />
                  </div>

                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    whileHover={!isMobile && !isSubmitting ? { scale: 1.02, y: -2 } : {}}
                    whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                    className="w-full theme-button-primary rounded-lg px-6 py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      boxShadow: `0 8px 24px ${theme.colors.accent}40`
                    }}
                  >
                    <Send className="w-5 h-5" />
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </motion.button>

                  <p className="text-center text-sm text-gray-400 mt-4">
                    We typically respond within 24 hours
                  </p>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="relative py-20 px-4">
        <SectionBackground />
        <div className="relative max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <div className="flex items-center justify-center mb-4">
              <HelpCircle className="w-10 h-10 text-orange-500 mr-3" />
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                Frequently Asked Questions
              </h2>
            </div>
            <p className="text-gray-400 text-center max-w-2xl mx-auto">
              Find answers to common questions about our courses, platform, and policies.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="border-b border-gray-800"
                >
                  <AccordionTrigger className="text-left text-white hover:text-orange-400 transition-colors duration-200 py-5">
                    <span className="text-lg font-medium pr-4">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400 pb-5 text-base leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* Still Need Help Section */}
      <section className="relative py-16 px-4">
        <SectionBackground />
        <div className="relative max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <Card className="border-0 bg-gradient-to-br from-orange-900/20 to-amber-900/20 backdrop-blur-sm">
              <CardHeader className="pb-6">
                <CardTitle className="text-2xl md:text-3xl text-white mb-2">
                  Still Need Help?
                </CardTitle>
                <CardDescription className="text-gray-300 text-base">
                  Can't find what you're looking for? Our support team is just an email away.
                </CardDescription>
              </CardHeader>
              <CardContent className="pb-8">
                <motion.button
                  onClick={() => {
                    document.getElementById('contact-form')?.scrollIntoView({ 
                      behavior: 'smooth',
                      block: 'start'
                    });
                  }}
                  whileHover={!isMobile ? { 
                    scale: 1.05,
                    y: -2
                  } : { scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="theme-button-primary rounded-full px-8 py-4 text-base font-semibold"
                  style={{
                    boxShadow: `0 8px 24px ${theme.colors.accent}40`
                  }}
                >
                  Contact Support →
                </motion.button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
      
      <CallToAction />
    </main>
  );
};

export default Support;

